import { useState } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '../components/Navigation'
import Logo from '../components/Logo'
import { apiClient } from '../services/apiClient'

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const validateEmail = (email: string): boolean => {
    return /\S+@\S+\.\S+/.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Email is required')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    try {
      await apiClient.forgotPassword(email)
      setSubmitted(true)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send reset email')
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-firedup-dark">
        <Navigation />
        
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <Logo className="w-20 h-20 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-white mb-2">Check Your Email</h1>
            </div>

            <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-4">Reset Link Sent!</h3>
                
                <p className="text-gray-300 mb-6">
                  We've sent a password reset link to <strong className="text-white">{email}</strong>. 
                  Please check your email and follow the instructions to reset your password.
                </p>
                
                <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-3 mb-6">
                  <p className="text-blue-300 text-sm">
                    <strong>Didn't receive the email?</strong> Check your spam folder, or try again in a few minutes.
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setSubmitted(false)
                      setEmail('')
                      setError('')
                    }}
                    className="w-full btn-secondary"
                  >
                    Send Another Email
                  </button>
                  
                  <Link 
                    to="/login" 
                    className="block w-full btn-primary text-center"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-firedup-dark">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <Logo className="w-20 h-20 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white mb-2">Forgot Password</h1>
            <p className="text-gray-400">Enter your email to receive a password reset link</p>
          </div>

          <div className="bg-firedup-gray/50 backdrop-blur-sm p-6 rounded-lg border border-firedup-light-gray">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`input-field ${error ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    if (error) setError('')
                  }}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </button>

              <div className="text-center space-y-2">
                <Link 
                  to="/login" 
                  className="block text-firedup-orange hover:text-firedup-red font-medium transition-colors text-sm"
                >
                  Back to Login
                </Link>
                <Link 
                  to="/register" 
                  className="block text-gray-400 hover:text-gray-300 transition-colors text-sm"
                >
                  Don't have an account? Sign up
                </Link>
              </div>
            </form>

            {/* Help Text */}
            <div className="mt-6 p-4 bg-firedup-dark/50 rounded border border-firedup-light-gray">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Need Help?</h4>
              <p className="text-xs text-gray-400">
                If you're having trouble with password reset, make sure you're using the same email 
                address you used to register your account. Password reset links expire after 1 hour 
                for security purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
